import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ChargeHelper from 'features/Charge/chargeHelper';
import KTFormInput, { KTFormInputType } from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTTooltip, { KTTooltipPositions } from 'general/components/OtherKeenComponents/KTTooltip';
import AppResource from 'general/constants/AppResource';
import UIHelper from 'general/helpers/UIHelper';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';

DialogWarningThresholdCabinetView.propTypes = {
  item: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

function DialogWarningThresholdCabinetView({ item, onCancel, onDone }) {
  // MARK: ---- PROPERTIES ----
  const [loading, setLoading] = useState(false);
  const [tempThreshold, setTempThreshold] = useState('');
  const [smokeThreshold, setSmokeThreshold] = useState('');
  const [waterThreshold, setWaterThreshold] = useState('');

  const checkValidNumberInput = useMemo(() => {
    return (value) => {
      const s = _.toString(value ?? '');
      if (s === '') return true;
      // allow digits + 1 decimal separator (either "." or ",")
      if (s.includes('.') && s.includes(',')) return false;
      return /^\d*([.,]\d*)?$/.test(s);
    };
  }, []);

  useEffect(() => {
    // Prefill existing thresholds (if any) from cabinet detail
    const t =
      item?.tempWarningThreshold ??
      item?.tempThreshold ??
      item?.temperatureWarningThreshold ??
      item?.temperatureThreshold;
    const s =
      item?.smokeWarningThreshold ??
      item?.smokeThreshold ??
      item?.smokeDensityWarningThreshold ??
      item?.smokeDensityThreshold;
    const w =
      item?.waterWarningThreshold ??
      item?.waterThreshold ??
      item?.floodWarningThreshold ??
      item?.floodThreshold;

    if (t !== null && t !== undefined && t !== '') setTempThreshold(_.toString(t));
    if (s !== null && s !== undefined && s !== '') setSmokeThreshold(_.toString(s));
    if (w !== null && w !== undefined && w !== '') setWaterThreshold(_.toString(w));
  }, [item]);

  // MARK: ---- METHODS ----
  const parseNumber = (raw) => {
    const s = _.toString(raw ?? '').trim();
    if (!Utils.isValid(s)) return null;
    return _.toNumber(s.replace(',', '.'));
  };

  const handleSubmit = async () => {
    const iTemp = parseNumber(tempThreshold);
    const iSmoke = parseNumber(smokeThreshold);
    const iWater = parseNumber(waterThreshold);

    if (iTemp === null || _.isNaN(iTemp)) {
      ToastHelper.showError('Vui lòng nhập ngưỡng cảnh báo nhiệt độ');
      return;
    }
    if (iSmoke === null || _.isNaN(iSmoke)) {
      ToastHelper.showError('Vui lòng nhập ngưỡng cảnh báo khói');
      return;
    }
    if (iWater === null || _.isNaN(iWater)) {
      ToastHelper.showError('Vui lòng nhập ngưỡng cảnh báo ngập nước');
      return;
    }

    const payload = _.omitBy(
      {
        // Most common field names on cabinet entity
        tempWarningThreshold: iTemp,
        smokeWarningThreshold: iSmoke,
        waterWarningThreshold: iWater,
      },
      (v) => v === null || v === undefined || v === ''
    );

    setLoading(true);
    const result = await ChargeHelper.API.requestUpdateCabinet(item?.pointId, payload);
    if (result?.result === 'success' || result === true) {
      ToastHelper.showSuccess('Thiết lập ngưỡng cảnh báo thành công');
      if (onDone) onDone();
    } else {
      ToastHelper.showError(_.isString(result) ? result : result?.reason || 'Thiết lập ngưỡng cảnh báo thất bại');
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="d-flex flex-column align-items-center justify-content-center text-center p-3 gap-2">
        <div className="mb-4">
          <img src={AppResource.vng.vngWarning} alt="warning" />
        </div>

        <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>
          Thiết lập ngưỡng cảnh báo
        </span>
        <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>
          Vui lòng nhập ngưỡng cảnh báo của tủ pin theo các tiêu chí bên dưới.
        </span>

        <div className="w-100 mt-4">
          <div className="text-left">
            <label
              className="d-flex align-items-center mb-1 text-left w-100"
              style={UIHelper.buildFontStyle('700', '13px', AppColors.gray[800])}
            >
              Ngưỡng cảnh báo nhiệt độ&nbsp;
              <span style={{ color: AppColors.danger }}>*</span>
              <KTTooltip
                position={KTTooltipPositions.topStart}
                textHeader="Ngưỡng cảnh báo nhiệt độ"
                text="Khi vượt quá mức nhiệt độ đã thiết lập, hệ thống sẽ thông báo đến nhà điều hành và chuyển nhiệt độ của tủ thành màu đỏ. Lúc này tủ sạc vẫn <strong>hoạt động bình thường</strong>."
                isHtmlContent
                offset={[0, 8]}
              >
                <i
                  className="fa-regular fa-circle-info"
                  style={{ color: AppColors.info, marginLeft: 2 }}
                />
              </KTTooltip>
            </label>
            <KTFormInput
              name="tempWarningThreshold"
              placeholder="Nhập số"
              type={KTFormInputType.text}
              unit="°C"
              value={tempThreshold}
              onChange={(value) => setTempThreshold(value)}
              checkValidInput={checkValidNumberInput}
            />
          </div>

          <div className="text-left mt-4">
            <label
              className="d-flex align-items-center mb-1 text-left w-100"
              style={UIHelper.buildFontStyle('700', '13px', AppColors.gray[800])}
            >
              Ngưỡng cảnh báo khói&nbsp;
              <span style={{ color: AppColors.danger }}>*</span>
              <KTTooltip
                position={KTTooltipPositions.topStart}
                textHeader="Ngưỡng cảnh báo khói"
                text="Khi vượt quá mức đã thiết lập, hệ thống sẽ chuyển trạng thái tủ pin thành <strong>Cảnh báo khói</strong> và vô hiệu hóa toàn bộ chức năng của tủ."
                isHtmlContent
                offset={[0, 8]}
              >
                <i
                  className="fa-regular fa-circle-info"
                  style={{ color: AppColors.info, marginLeft: 2 }}
                />
              </KTTooltip>
            </label>
            <KTFormInput
              name="smokeWarningThreshold"
              placeholder="Nhập số"
              type={KTFormInputType.text}
              unit="%/m"
              value={smokeThreshold}
              onChange={(value) => setSmokeThreshold(value)}
              checkValidInput={checkValidNumberInput}
            />
          </div>

          <div className="text-left mt-4">
            <label
              className="d-flex align-items-center mb-1 text-left w-100"
              style={UIHelper.buildFontStyle('700', '13px', AppColors.gray[800])}
            >
              Ngưỡng cảnh báo ngập nước&nbsp;
              <span style={{ color: AppColors.danger }}>*</span>
              <KTTooltip
                position={KTTooltipPositions.topStart}
                textHeader="Ngưỡng cảnh báo ngập nước"
                text="Khi vượt quá mức đã thiết lập, hệ thống sẽ chuyển trạng thái tủ pin thành <strong>Cảnh báo ngập nước</strong> và vô hiệu hóa toàn bộ chức năng của tủ."
                isHtmlContent
                offset={[0, 8]}
              >
                <i
                  className="fa-regular fa-circle-info"
                  style={{ color: AppColors.info, marginLeft: 2 }}
                />
              </KTTooltip>
            </label>
            <KTFormInput
              name="waterWarningThreshold"
              placeholder="Nhập số"
              type={KTFormInputType.text}
              unit="cm"
              value={waterThreshold}
              onChange={(value) => setWaterThreshold(value)}
              checkValidInput={checkValidNumberInput}
            />
          </div>
        </div>
      </div>

      <div
        className="d-flex align-items-center gap-3 p-3 w-100"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null, true)}
        {UIHelper.buildGreenButton('Thiết lập', handleSubmit, null, true, loading)}
      </div>
    </div>
  );
}

export default DialogWarningThresholdCabinetView;
